import React, { useState, useContext } from 'react';
import { Typography, Paper, Button, TextField, InputAdornment, IconButton } from '@material-ui/core';
import { Save, Visibility, VisibilityOff } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { changePassword, adminChangePassword } from '../../utils/AccountsAPI';
import { toastError, toastSuccess } from '../../utils/Functions';
import EmployeeInfoContext from '../../contexts/EmployeeInfoContext';
import { trackPromise } from 'react-promise-tracker';

const useStyles = makeStyles((theme) => ({
  root: {},
  rootPaper: {
    padding: theme.spacing(3),
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: '75vw',
      margin: theme.spacing(3) + 'px auto',
    },
    display: 'inline-block',
  },
  paperHeader: { display: 'flex' },
  paperTitle: {
    marginLeft: 0,
    marginRight: 'auto',
  },
  upperRightIcon: {},
  paperContent: {
    paddingBottom: theme.spacing(1),
  },
  actionButton: {
    margin: theme.spacing(1),
  },
  actionContainer: {
    margin: 'auto',
    float: 'right',
    display: 'inline-flex',
  },
  icon: {
    paddingRight: theme.spacing(1),
  },
}));

export default function ResetPassword(props) {
  const classes = useStyles();
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showPasswords, setShowPasswords] = useState<boolean>(false);
  const [checkErrors, setCheckErrors] = useState<boolean>(false);

  const [employeeInfo] = useContext(EmployeeInfoContext);
  const user = props.user ?? employeeInfo;

  const userId = user?.id ?? '';
  const requireCurrent = props.requireCurrent;

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setCheckErrors(false);
    setShowPasswords(false);
  };

  const onChange = (e) => {
    switch (e.target.id) {
      case 'currentPassword':
        setCurrentPassword(e.target.value);
        break;
      case 'newPassword':
        setNewPassword(e.target.value);
        break;
      case 'confirmPassword':
        setConfirmPassword(e.target.value);
        break;
    }
  };

  const onBlur = (e) => {
    setCheckErrors(e.target.value !== '');
  };

  const savePassword = () => {
    if (newPassword === confirmPassword) {
      let promise;
      if (requireCurrent) promise = changePassword(userId, currentPassword, newPassword);
      else promise = adminChangePassword(userId, newPassword);

      trackPromise(promise)
        .then((response) => {
          toastSuccess(props.toast, 'Successfully changed password!');
          reset();
        })
        .catch((err) => toastError(props.toast, err));
    } else toastError(props.toast, 'Passwords do not match!');
  };

  return (
    <Paper className={classes.rootPaper}>
      <div className={classes.paperHeader}>
        <Typography variant='h4' className={classes.paperTitle}>
          {'Change Password - ' + user?.userName}
        </Typography>
      </div>

      <form onSubmit={savePassword}>
        <div className={classes.paperContent}>
          {requireCurrent ? (
            <TextField
              id='currentPassword'
              label='Current Password'
              value={currentPassword}
              onChange={onChange}
              fullWidth
              type={showPasswords ? 'text' : 'password'}
              autoComplete='current-password'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton onClick={() => setShowPasswords((a) => !a)} onMouseDown={(e) => e.preventDefault()}>
                      {showPasswords ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ) : null}
          <TextField
            id='newPassword'
            label='New Password'
            value={newPassword}
            onChange={onChange}
            fullWidth
            helperText={'Required: Uppercase, Lowercase, Digit, Symbol, 6+ characters.'}
            type={showPasswords ? 'text' : 'password'}
            autoComplete='new-password'
            disabled={!userId}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton onClick={() => setShowPasswords((a) => !a)} onMouseDown={(e) => e.preventDefault()}>
                    {showPasswords ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            id='confirmPassword'
            label='Confirm Password'
            value={confirmPassword}
            onChange={onChange}
            onBlur={onBlur}
            fullWidth
            error={checkErrors && newPassword !== confirmPassword}
            helperText={checkErrors && newPassword !== confirmPassword ? 'Passwords do not match.' : undefined}
            type={showPasswords ? 'text' : 'password'}
            autoComplete='new-password'
            onKeyDown={(e) => {
              if (e.keyCode === 13) savePassword();
            }}
            disabled={!userId}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton onClick={() => setShowPasswords((a) => !a)} onMouseDown={(e) => e.preventDefault()}>
                    {showPasswords ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div className={classes.actionContainer}>
          <Button color='primary' variant='contained' className={classes.actionButton} onClick={savePassword}>
            <Save className={classes.icon} />
            Save
          </Button>
        </div>
      </form>
    </Paper>
  );
}
